import { motion } from 'framer-motion';
import { useRef } from 'react';

type Particle = {
  id: number;
  x: number;
  y: number;
  size: number;
  delay: number;
  moveX: number;
  moveY: number;
  rotate: number;
  duration: number;
};

const ParticleAnimation = () => {
  // Generate persistent random configurations
  const particles = useRef<Particle[]>(
    Array.from({ length: 50 }, (_, i) => {
      const angle = Math.random() * Math.PI * 2;
      const radius = Math.random() * 30; // Reduced radius to prevent edge collision
      const size = Math.random() * 4 + 2;

      // Calculate safe starting position (accounting for particle size)
      const safeMargin = size + 5; // Extra margin for safety
      const startX =
        Math.random() * (200 - 2 * safeMargin) - (100 - safeMargin);
      const startY =
        Math.random() * (200 - 2 * safeMargin) - (100 - safeMargin);

      // Calculate movement that won't exceed boundaries
      const moveX = Math.cos(angle) * radius;
      const moveY = Math.sin(angle) * radius;

      // Ensure movement doesn't cause the particle to go outside bounds
      const maxMoveX = Math.min(
        Math.abs(moveX),
        100 - safeMargin - Math.abs(startX)
      );
      const maxMoveY = Math.min(
        Math.abs(moveY),
        100 - safeMargin - Math.abs(startY)
      );

      return {
        id: i,
        x: startX,
        y: startY,
        size: size,
        delay: Math.random() * 2,
        moveX: moveX > 0 ? maxMoveX : -maxMoveX,
        moveY: moveY > 0 ? maxMoveY : -maxMoveY,
        rotate: Math.random() * 360,
        duration: 2 + Math.random() * 2,
      };
    })
  ).current;

  return (
    <svg viewBox='-100 -100 200 200'>
      {particles.map((particle) => (
        <motion.circle
          key={particle.id}
          cx={particle.x}
          cy={particle.y}
          r={particle.size}
          fill='#3B82F6'
          initial={{
            opacity: 0,
            scale: 0,
            translateX: 0,
            translateY: 0,
          }}
          animate={{
            opacity: [0, 1, 0],
            scale: [0, 1, 0.5],
            translateX: [0, particle.moveX, 0],
            translateY: [0, particle.moveY, 0],
            rotate: [0, particle.rotate],
          }}
          transition={{
            duration: particle.duration,
            repeat: Infinity,
            delay: particle.delay,
            ease: 'anticipate',
            times: [0, 0.5, 1],
            repeatType: 'mirror',
          }}
        />
      ))}
    </svg>
  );
};

export default ParticleAnimation;
